import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import agentsService from "../services/agentsService";
import toast from "react-hot-toast";

// Get all agents hook
export const useAgents = () => {
  return useQuery({
    queryKey: ["agents"],
    queryFn: agentsService.getAgents,
    staleTime: 60000, // Consider data stale after 1 minute
    cacheTime: 300000, // Keep in cache for 5 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Get single agent hook
export const useAgent = (id) => {
  return useQuery({
    queryKey: ["agent", id],
    queryFn: () => agentsService.getAgent(id),
    enabled: !!id, // Only run if id is provided
    staleTime: 60000,
    cacheTime: 300000,
    retry: 2,
  });
};

// Create agent mutation
export const useCreateAgent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: agentsService.createAgent,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      toast.success("Agent created successfully!");
    },
    onError: (error) => {
      toast.error("Failed to create agent");
      console.error("Create agent error:", error);
    },
  });
};

// Update agent mutation
export const useUpdateAgent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => agentsService.updateAgent(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.invalidateQueries({ queryKey: ["agent", data.id] });
      toast.success("Agent updated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to update agent");
      console.error("Update agent error:", error);
    },
  });
};

// Delete agent mutation
export const useDeleteAgent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: agentsService.deleteAgent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      toast.success("Agent deleted successfully!");
    },
    onError: (error) => {
      toast.error("Failed to delete agent");
      console.error("Delete agent error:", error);
    },
  });
};

// Test agent mutation
export const useTestAgent = () => {
  return useMutation({
    mutationFn: ({ id, message }) => agentsService.testAgent(id, message),
    onError: (error) => {
      toast.error("Failed to test agent");
      console.error("Test agent error:", error);
    },
  });
};
